/**
 * Which product a name means, the way a shopping list or a recipe writes it: "Kirschtomaten",
 * "Himbeereis", "Bohnen, grün", "Tomaten, stückig (Dose)", "Spinat (TK)". Taken over from the
 * EinkaufsZettel (lookup in packages/core/src/catalog.ts there) rule for rule, on product ids;
 * SpeisePlan follows the same rules in its emoji.ts. tools/test/parity.test.ts compares all three
 * whenever the sister repos lie beside this one.
 *
 * Unlike search() this never guesses at typos: a name is known, or it is not.
 */
import { PRODUCTS, aisle } from "./catalog.ts";
import type { Product } from "./catalog.ts";
import { fold, keysOf, stem } from "./normalize.ts";

/** The aisle every name lands in that the catalogue does not know. */
export const FALLBACK_AISLE = "sonstiges";

const INDEX = new Map<string, Product>();
for (const p of PRODUCTS) {
  for (const name of p.names) for (const key of keysOf(name)) if (!INDEX.has(key)) INDEX.set(key, p);
}

const WRITTEN = new Set(PRODUCTS.flatMap((p) => p.names.flatMap((name) => [fold(name), fold(name).replaceAll(" ", "")])));

/** Heads of two letters that still decide a compound: Distel|öl, Rühr|ei. */
const SHORT_HEADS = new Set(["ol", "ei"]);
/** Heads that say less than what comes before them: Kabeljau|filet is fish, not meat. */
const GENERIC_HEADS = new Set(["filet"]);
/** Fronts that decide where it is sold, whatever follows: Tiefkühl|spinat, Dosen|pfirsiche. */
const DECIDING_FRONTS = new Set(["tiefkuhl", "tk", "dose", "dosen"]);

function exact(phrase: string): Product | undefined {
  return INDEX.get(phrase) ?? INDEX.get(phrase.split(" ").map(stem).join(" "));
}

/** A modifier in front of a head, allowing for the joints German puts between them (Rind|er|, Schwein|e|). */
function knownPrefix(prefix: string): Product | undefined {
  if (prefix.length < 3) return undefined;
  for (const form of [prefix, stem(prefix), prefix.replace(/s$/, ""), prefix.replace(/er$/, ""), prefix.replace(/n$/, "")]) {
    const hit = form.length >= 2 ? exact(form) : undefined;
    if (hit) return hit;
  }
  return undefined;
}

/**
 * A German compound decides by its last part: Kirsch|tomaten, Voll|milch, Himbeer|eis. Splits whose
 * front part is a known word too win (Erdbeer|eis, not Erdbee|reis); otherwise the longest known
 * ending.
 */
function compound(word: string): Product | undefined {
  let longest: Product | undefined;
  for (let i = 1; i < word.length - 1; i += 1) {
    const head = word.slice(i);
    if (head.length < 3 && !SHORT_HEADS.has(head)) continue;
    // laktosefrei, zuckerfrei: an adjective, not an egg.
    if (head === "ei" && word.endsWith("frei")) continue;
    const hit = exact(head);
    if (!hit) continue;
    const front = knownPrefix(word.slice(0, i));
    if (front) return GENERIC_HEADS.has(stem(head)) || DECIDING_FRONTS.has(word.slice(0, i)) ? front : hit;
    longest ??= hit;
  }
  return longest;
}

/**
 * An adjective after the noun, the other way round: "Bohnen grün" is "Grüne Bohnen". In front it takes
 * an ending the stem does not always drop again: "Mandeln, gemahlen" are "Gemahlene Mandeln".
 */
function reversed(noun: string, adjective: string): string[] {
  return [`${adjective} ${noun}`, `${adjective}e ${noun}`];
}

/** The product for the folded words of a name. */
function find(words: string[]): Product | undefined {
  // The whole name, pairs of words in either order, then single words from the front: in "Joghurt
  // Erdbeere" or "Frischkäse Kräuter" the product comes first.
  const phrases = [words.join(" ")];
  if (words.length === 2) phrases.push(...reversed(words[0]!, words[1]!));
  if (words.length > 2) phrases.push(words.slice(-2).join(" "), ...reversed(words.at(-2)!, words.at(-1)!), words.slice(0, 2).join(" "));
  if (words.length > 1) phrases.push(...words);
  let found: Product | undefined;
  for (const phrase of phrases) {
    found = exact(phrase);
    if (found) break;
  }
  if (!found) {
    for (let i = words.length - 1; i >= 0 && !found; i -= 1) {
      if (words[i]!.length >= 3) found = compound(words[i]!);
    }
  }
  return found;
}

/**
 * A can or a jar says more than the name: "1 Dose Kraut", "1 Glas Gurken", "2 Dosen Kichererbsen"
 * stand with the tins, not with the fresh vegetables, the frozen peas or the dry pulses.
 */
const TINNED: Readonly<Record<string, readonly string[]>> = {
  Dose: ["obst", "tk", "nudeln", "fleisch", "fisch"],
  Glas: ["obst", "tk", "fleisch"],
};

/** The aisle once the packaging is known (the units "Dose" and "Glas"); for a learned aisle as well as the catalogue's. */
export function packagedAisle(aisleId: string, unit: string | null | undefined): string {
  return unit && TINNED[unit]?.includes(aisleId) ? "konserven" : aisleId;
}

/**
 * What a household learns from an item's aisle: the aisle without the packaging. After "1 Dose Kraut"
 * with the tins, fresh "Kraut" still goes to the vegetables. `usual` is the name's aisle so far.
 */
export function unpackagedAisle(aisleId: string, usual: string, unit: string | null | undefined): string {
  return aisleId !== usual && packagedAisle(usual, unit) === aisleId ? usual : aisleId;
}

/**
 * A packing in the name itself, as recipes write it: "Tomaten, stückig (Dose)", "Spinat (TK)",
 * "Gurken im Glas". It moves the product like the unit would, and a weight does not undo that: 400 g
 * of "Tomaten (Dose)" are still a tin.
 */
const PACKING = new Map<string, (aisleId: string) => string>([
  ["dose", (a) => packagedAisle(a, "Dose")],
  ["dosen", (a) => packagedAisle(a, "Dose")],
  ["glas", (a) => packagedAisle(a, "Glas")],
  ["tk", () => "tk"],
  ["tiefkuhl", () => "tk"],
]);

export interface Match {
  product: Product;
  /** The aisle, moved by a packing in the name ("(Dose)", "TK"). */
  aisle: string;
  /** The product's emoji, else that of the aisle. */
  emoji: string;
}

/** The product a name means, with its aisle; undefined when the catalogue knows nothing about it. */
export function lookup(name: string): Match | undefined {
  const words = fold(name).split(" ").filter(Boolean);
  if (words.length === 0) return undefined;
  // Beside a packing, the other words name the product; names the catalogue knows whole ("TK Pizza")
  // stay whole, and a name of nothing but packing words ("Dosen") is looked up as it is.
  const packing = words.length > 1 && !exact(words.join(" ")) ? words.find((word) => PACKING.has(word)) : undefined;
  const product = (packing ? find(words.filter((word) => !PACKING.has(word))) : undefined) ?? find(words);
  if (!product) return undefined;
  const aisleId = packing ? PACKING.get(packing)!(product.aisle) : product.aisle;
  return { product, aisle: aisleId, emoji: product.emoji ?? aisle(aisleId)!.emoji };
}

/** The aisle of a name, bought by the can or the jar or not; "sonstiges" when it is unknown. */
export function aisleForName(name: string, unit: string | null = null): string {
  const match = lookup(name);
  return match ? packagedAisle(match.aisle, unit) : FALLBACK_AISLE;
}

/**
 * The emoji beside a name as a recipe or a list writes it: the product's own, else that of its aisle
 * (by the can with the tins). Null when the catalogue does not know the name: a box for "other"
 * says nothing beside an ingredient.
 */
export function emojiForName(name: string, unit: string | null = null): string | null {
  const match = lookup(name);
  if (!match) return null;
  return match.product.emoji ?? aisle(packagedAisle(match.aisle, unit))?.emoji ?? null;
}

/**
 * The product a name stands for exactly: any of its names, singular or plural, with or without
 * umlauts and spaces ("Äpfel", "apfel", "Rote Zwiebeln", "rotezwiebel"). No compounds, no single
 * words of a longer name ("Bio-Kirschtomaten"): that is lookup().
 */
export function findProduct(name: string): Product | undefined {
  if (fold(name) === "") return undefined;
  for (const key of keysOf(name)) {
    const hit = INDEX.get(key);
    if (hit) return hit;
  }
  return undefined;
}

/** Whether the catalogue has exactly this name (no compounds, no single words of a phrase). */
export function isKnownName(name: string): boolean {
  const folded = fold(name);
  return folded !== "" && exact(folded) !== undefined;
}

/** Whether the name is written like one of the catalogue (only case, accents and spaces may differ; no plural). */
export function isCatalogSpelling(name: string): boolean {
  return WRITTEN.has(fold(name));
}
